import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { AUTH_BG } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/States";
import ElmMark, { ElmGlyph } from "@/components/ElmMark";
import PlanCards, { IntervalToggle } from "@/components/billing/PlanCards";

const WHO = [
  ["Renovation & custom-home contractors", "Done running jobs from texts, the truck and your own head."],
  ["Design-build firms", "Design, schedule, costs and client approvals in one place."],
  ["Crews & trades", "Today's plan, their scope and dates — without chasing the boss."],
  ["Owner-builders", "Running your own build and staying on top of trades, budget and decisions."],
];

const POINTS = [
  ["Schedule & crew", "List, week, month and timeline views. Assign trades and crew, and everyone sees their own week."],
  ["Trade Pass & Site Binder", "A private link and QR code per trade: their scope, dates, site rules and safety docs. No login needed."],
  ["Money that adds up", "Estimates from your price book, receipt scanning, expenses by job, and hours turned into invoices. QuickBooks ready."],
  ["A window for clients", "Clients see progress, photos, schedule and budget at the level you choose, add ideas to Wish List, and weigh in on design picks."],
];

export default function Landing() {
  const plans = useFetch("/public/plans");
  const [interval, setInterval] = useState("monthly");
  const nav = useNavigate();
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <header className="flex items-center justify-between px-6 py-5 sm:px-12">
        <p className="flex items-center gap-2 font-serif text-3xl text-primary"><ElmGlyph className="h-7 w-7" />E.L.M</p>
        <nav className="flex items-center gap-2">
          <Button asChild variant="ghost" data-testid="landing-login-link"><Link to="/login">Sign in</Link></Button>
          <Button asChild className="rounded-full" data-testid="landing-signup-link"><Link to="/signup">Start free trial</Link></Button>
        </nav>
      </header>
      <section className="grain relative mx-6 overflow-hidden rounded-3xl sm:mx-12">
        <img src={AUTH_BG} alt="" className="h-[460px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-8 text-white sm:p-14">
          <p className="text-xs uppercase tracking-[0.3em] text-white/70">Elevated Living Management</p>
          <h1 className="mt-3 max-w-2xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Run every build, crew and client from one place.</h1>
          <p className="mt-4 max-w-lg text-sm text-white/85 sm:text-base">Schedules, trade passes, expenses, timesheets and a client window into every job, branded as your company. Design boards included. 14 days free, no card needed.</p>
          <Button asChild size="lg" className="mt-8 w-fit rounded-full" data-testid="landing-hero-cta"><Link to="/signup">Create your workspace <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>
      <section className="grid gap-8 px-6 py-20 sm:grid-cols-2 sm:px-12 lg:grid-cols-4">
        {POINTS.map(([t, d], i) => (
          <div key={t} className="rise" style={{ animationDelay: `${i * 80}ms` }}>
            <p className="eyebrow">0{i + 1}</p><h2 className="mt-3 font-serif text-2xl">{t}</h2><p className="mt-2 text-sm text-muted-foreground">{d}</p>
          </div>
        ))}
      </section>
      <section className="px-6 py-20 sm:px-12" data-testid="landing-who">
        <div className="max-w-2xl">
          <p className="eyebrow">Who it's for</p>
          <h2 className="mt-2 font-serif text-4xl sm:text-5xl">Built for builders who want to run it like a business.</h2>
          <p className="mt-4 text-base text-muted-foreground">E.L.M rewards builders who want to stay organized — plan ahead, track as you go, hand over something polished. Put a little in and it pays you back on every job.</p>
        </div>
        <div className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2">
          {WHO.map(([t, d], i) => (
            <div key={t} className="rise border-l-2 border-accent pl-5" style={{ animationDelay: `${i * 80}ms` }}>
              <h3 className="font-serif text-2xl">{t}</h3><p className="mt-1.5 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 max-w-2xl border-t pt-6 text-sm text-muted-foreground" data-testid="landing-who-honest">
          <span className="font-medium text-foreground">And who it isn't:</span> if you'd rather wing it and sort the details out later, E.L.M will feel like work you don't want to do. We'd rather tell you now. For the organized builder, though, it's an unfair advantage.
        </p>
      </section>
      <section className="space-y-8 px-6 pb-24 sm:px-12" id="pricing">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="eyebrow">Pricing</p><h2 className="mt-2 font-serif text-4xl sm:text-5xl">Simple plans, in Canadian dollars</h2></div>
          <IntervalToggle value={interval} onChange={setInterval} />
        </div>
        {plans.data ? <PlanCards plans={plans.data} interval={interval} onChoose={() => nav("/signup")} cta="Start with" /> : <Loading rows={2} />}
      </section>
      <footer className="flex flex-wrap items-center justify-between gap-4 border-t px-6 py-8 sm:px-12"><ElmMark /><p className="flex items-center gap-4 text-xs text-muted-foreground"><span>Built for contractors, renovators and the people who hire them.</span><Link to="/terms" className="underline underline-offset-4" data-testid="landing-terms-link">Terms</Link><Link to="/privacy" className="underline underline-offset-4" data-testid="landing-privacy-link">Privacy</Link></p></footer>
    </div>
  );
}
